import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { EmptyState } from '@/components/EmptyState';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { me } from '@/lib/auth';
import { formatDate } from '@/lib/locale';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

type Recipient = {
  id: string;
  email: string;
  display_name?: string;
  receive_weekly: boolean;
  receive_monthly: boolean;
  receive_alerts_critical: boolean;
  receive_alerts_warning: boolean;
  receive_onboarding: boolean;
  bounce_count?: number;
  active?: boolean;
  created_at?: string;
};

type RecipientInput = Omit<Recipient, 'id' | 'bounce_count' | 'active' | 'created_at'>;

const FLAG_LABELS: Array<{
  key: keyof Pick<
    Recipient,
    | 'receive_weekly'
    | 'receive_monthly'
    | 'receive_alerts_critical'
    | 'receive_alerts_warning'
    | 'receive_onboarding'
  >;
  label: string;
}> = [
  { key: 'receive_weekly', label: 'Wekelijks rapport' },
  { key: 'receive_monthly', label: 'Maandelijks rapport' },
  { key: 'receive_alerts_critical', label: 'Kritieke alerts' },
  { key: 'receive_alerts_warning', label: 'Warning alerts' },
  { key: 'receive_onboarding', label: 'Onboarding e-mails' },
];

const EMPTY_INPUT: RecipientInput = {
  email: '',
  display_name: '',
  receive_weekly: true,
  receive_monthly: true,
  receive_alerts_critical: true,
  receive_alerts_warning: false,
  receive_onboarding: false,
};

export function RecipientsManage({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: () => me() });
  const canWrite = meQ.data?.platform_role === 'admin' || meQ.data?.platform_role === 'operator';

  const listQ = useQuery({
    queryKey: ['recipients', slug],
    queryFn: () => apiGet<Recipient[]>(`/tenants/${slug}/recipients`, { tenant: slug }),
  });

  const [editing, setEditing] = useState<Recipient | 'new' | null>(null);
  const [draft, setDraft] = useState<RecipientInput>(EMPTY_INPUT);

  const openCreate = () => {
    setEditing('new');
    setDraft(EMPTY_INPUT);
  };
  const openEdit = (r: Recipient) => {
    setEditing(r);
    setDraft({
      email: r.email,
      display_name: r.display_name ?? '',
      receive_weekly: r.receive_weekly,
      receive_monthly: r.receive_monthly,
      receive_alerts_critical: r.receive_alerts_critical,
      receive_alerts_warning: r.receive_alerts_warning,
      receive_onboarding: r.receive_onboarding,
    });
  };
  const close = () => setEditing(null);

  const createMut = useMutation({
    mutationFn: (input: RecipientInput) =>
      apiPost<Recipient>(`/tenants/${slug}/recipients`, input, { tenant: slug }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipients', slug] });
      close();
    },
  });
  const updateMut = useMutation({
    mutationFn: (input: { id: string; patch: Partial<RecipientInput> }) =>
      apiPatch<Recipient>(`/tenants/${slug}/recipients/${input.id}`, input.patch, { tenant: slug }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipients', slug] });
      close();
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/tenants/${slug}/recipients/${id}`, { tenant: slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recipients', slug] }),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing === 'new') {
      createMut.mutate(draft);
    } else if (editing) {
      updateMut.mutate({ id: editing.id, patch: draft });
    }
  };

  const rows = listQ.data ?? [];

  return (
    <div className="max-w-[1300px] space-y-5 p-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Klantcontacten</h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {rows.length} ontvangers · per persoon configureerbaar welk rapport
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-slate-900 hover:bg-brand-500"
          >
            <Plus size={12} />
            Contact toevoegen
          </button>
        )}
      </div>

      <QueryState
        isLoading={listQ.isLoading}
        isError={listQ.isError}
        onRetry={() => listQ.refetch()}
      >
        {rows.length === 0 ? (
          <Card>
            <EmptyState
              title="Geen klantcontacten geconfigureerd"
              body="Rapporten en alerts worden alleen verstuurd naar gedefinieerde ontvangers. Voeg minstens één e-mailadres toe."
            />
          </Card>
        ) : (
          <Card>
            <CardHeader title="Ontvangers" subtitle={`${rows.length} adressen`} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>Naam / e-mail</Th>
                    {FLAG_LABELS.map((f) => (
                      <Th key={f.key} align="center">
                        {shortFlagLabel(f.key)}
                      </Th>
                    ))}
                    <Th>Bounces</Th>
                    <Th>Aangemaakt</Th>
                    {canWrite && <Th align="right">Actie</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className={`hover:bg-slate-50 ${r.active === false ? 'opacity-60' : ''}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-900">{r.display_name || '—'}</div>
                        <div className="font-mono text-[11px] text-slate-500">{r.email}</div>
                      </td>
                      {FLAG_LABELS.map((f) => (
                        <td key={f.key} className="px-3 py-2.5 text-center">
                          <FlagDot on={r[f.key]} />
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-slate-500">{r.bounce_count ?? 0}</td>
                      <td className="px-3 py-2.5 text-slate-500">
                        {r.created_at ? formatDate(r.created_at) : '—'}
                      </td>
                      {canWrite && (
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            aria-label="Bewerken"
                            className="mr-2 text-slate-500 hover:text-slate-900"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(`Verwijder ${r.email}?`)) {
                                deleteMut.mutate(r.id);
                              }
                            }}
                            aria-label="Verwijderen"
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </QueryState>

      {editing !== null && (
        <Dialog
          title={editing === 'new' ? 'Contact toevoegen' : 'Contact bewerken'}
          onClose={close}
        >
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                E-mail
              </label>
              <input
                required
                type="email"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                className="mt-1 h-8 w-full rounded border border-slate-200 bg-white px-2 text-[13px] focus:border-slate-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Weergavenaam (optioneel)
              </label>
              <input
                type="text"
                value={draft.display_name ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
                className="mt-1 h-8 w-full rounded border border-slate-200 bg-white px-2 text-[13px] focus:border-slate-400 focus:outline-none"
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Welke berichten ontvangen?
              </p>
              {FLAG_LABELS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-[13px] text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft[f.key]}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.checked }))}
                  />
                  {f.label}
                </label>
              ))}
            </div>
            {(createMut.isError || updateMut.isError) && (
              <div
                role="alert"
                className="rounded border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
              >
                Opslaan mislukt. Probeer opnieuw of bekijk de logs.
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                className="rounded border border-slate-200 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="rounded bg-brand-600 px-3 py-1.5 text-[13px] font-medium text-slate-900 hover:bg-brand-500 disabled:opacity-50"
              >
                {createMut.isPending || updateMut.isPending ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
}

function FlagDot({ on }: { on: boolean }) {
  return on ? (
    <span className="inline-block h-2 w-2 rounded-full bg-green-600" aria-label="aan" />
  ) : (
    <span className="inline-block h-2 w-2 rounded-full bg-slate-200" aria-label="uit" />
  );
}

function shortFlagLabel(key: string): string {
  switch (key) {
    case 'receive_weekly':
      return 'Wkl';
    case 'receive_monthly':
      return 'Mnd';
    case 'receive_alerts_critical':
      return 'Crit';
    case 'receive_alerts_warning':
      return 'Warn';
    case 'receive_onboarding':
      return 'Onb';
  }
  return key;
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="bg-slate-900/40 fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-[480px] max-w-[95vw] overflow-auto rounded-md bg-white shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-900"
          >
            ×
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
